import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {ScalarMathNodeOperation} from "#material/nodes/converter/scalar-math/constants"
import type {ScalarMathSockets} from "#material/nodes/converter/scalar-math/definition"
import {scalarMathDefinition} from "#material/nodes/converter/scalar-math/definition"
import {
    convertOperationToMathType,
    flooredModulo,
    pingPong,
    safeDivide,
    smoothMinimum,
    withinDistance,
    wrapped,
} from "#material/nodes/converter/scalar-math/helpers"
import type {CyclesReturns} from "#material/sockets/cycles"
import {threeConvert, threeValue} from "#material/three/three-utils"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import {ThreeTsl as TSL} from "#material/three/exports"
import type {ThreeReturns} from "#material/sockets/three"
import type {MathNode} from "#material/cycles/types"

@registerMaterialGraphNode
export class ScalarMath extends MaterialNode<ScalarMathSockets> {
    public override get definition() {
        return scalarMathDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<ScalarMathSockets>> => {
        const {
            value: valueA,
            value_001: valueB,
            value_002: valueC,
        } = await evaluator.getAll({
            value: this.parameters.inputs.value,
            value_001: this.parameters.inputs.value_001,
            value_002: this.parameters.inputs.value_002,
        })
        const inputA = valueA?.toFloat() ?? threeConvert(this.parameters.settings.value, threeValue) ?? threeValue(0)
        const inputB = valueB?.toFloat() ?? threeConvert(this.parameters.settings.value_001, threeValue) ?? threeValue(0)
        const inputC = valueC?.toFloat() ?? threeConvert(this.parameters.settings.value_002, threeValue) ?? threeValue(0)
        const {useClamp} = this.parameters.settings
        const operation = this.parameters.settings.operation ?? "ADD"

        const getValue = (operation: ScalarMathNodeOperation) => {
            switch (operation) {
                case "ADD":
                    return TSL.add(inputA, inputB)
                case "SUBTRACT":
                    return TSL.sub(inputA, inputB)
                case "MULTIPLY":
                    return TSL.mul(inputA, inputB)
                case "DIVIDE":
                    return TSL.div(inputA, inputB)
                case "POWER":
                    return TSL.pow(inputA, inputB)
                case "LOGARITHM":
                    return TSL.log(inputA)
                case "SQRT":
                    return TSL.sqrt(inputA)
                case "INVERSE_SQRT":
                    return TSL.inverseSqrt(inputA)
                case "ABSOLUTE":
                    return TSL.abs(inputA)
                case "EXPONENT":
                    return TSL.exp(inputA)
                case "MINIMUM":
                    return TSL.min(inputA, inputB)
                case "MAXIMUM":
                    return TSL.max(inputA, inputB)
                case "LESS_THAN":
                    return TSL.lessThan(inputA, inputB).toFloat()
                case "GREATER_THAN":
                    return TSL.greaterThan(inputA, inputB).toFloat()
                case "SIGN":
                    return TSL.sign(inputA)
                case "ROUND":
                    return TSL.round(inputA)
                case "FLOOR":
                    return TSL.floor(inputA)
                case "CEIL":
                    return TSL.ceil(inputA)
                case "TRUNC":
                    return TSL.trunc(inputA)
                case "FRACT":
                    return TSL.fract(inputA)
                case "MODULO":
                    return TSL.mod(inputA, inputB)
                case "SINE":
                    return TSL.sin(inputA)
                case "COSINE":
                    return TSL.cos(inputA)
                case "TANGENT":
                    return TSL.tan(inputA)
                case "ARCSINE":
                    return TSL.asin(inputA)
                case "ARCCOSINE":
                    return TSL.acos(inputA)
                case "ARCTANGENT":
                    return TSL.atan(inputA)
                case "ARCTAN2":
                    return TSL.atan(inputA, inputB)
                case "RADIANS":
                    return TSL.radians(inputA)
                case "DEGREES":
                    return TSL.degrees(inputA)
                case "MULTIPLY_ADD":
                    return TSL.add(TSL.mul(inputA, inputB), inputC)
                case "COMPARE":
                    return withinDistance(inputA, inputB, inputC)
                case "SMOOTH_MIN":
                    return smoothMinimum(inputA, inputB, inputC)
                case "SMOOTH_MAX":
                    return TSL.negate(smoothMinimum(TSL.negate(inputA), TSL.negate(inputB), inputC))
                case "FLOORED_MODULO":
                    return flooredModulo(inputA, inputB)
                case "WRAP":
                    return wrapped(inputA, inputB, inputC)
                case "SNAP":
                    return TSL.mul(TSL.floor(safeDivide(inputA, inputB)), inputB)
                case "PINGPONG":
                    return pingPong(inputA, inputB)
                case "SINH":
                    return TSL.sinh(inputA)
                case "COSH":
                    return TSL.cosh(inputA)
                case "TANH":
                    return TSL.tanh(inputA)
                default:
                    throw new Error(`Invalid operation: ${operation}`)
            }
        }

        const value = getValue(operation as ScalarMathNodeOperation)
        return {value: useClamp ? TSL.clamp(value, threeValue(0), threeValue(1)) : value}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<ScalarMathSockets>> => {
        const operation = this.parameters.settings.operation as ScalarMathNodeOperation
        const {value1Input, value2Input, value3Input} = await evaluator.getAll({
            value1Input: this.parameters.inputs.value,
            value2Input: this.parameters.inputs.value_001,
            value3Input: this.parameters.inputs.value_002,
        })

        const mathNode: MathNode = {
            type: "math",
            inputs: {value1: value1Input, value2: value2Input, value3: value3Input},
            parameters: {
                math_type: convertOperationToMathType(operation),
                value1: value1Input === undefined ? this.parameters.settings.value : undefined,
                value2: value2Input === undefined ? this.parameters.settings.value_001 : undefined,
                value3: value3Input === undefined ? this.parameters.settings.value_002 : undefined,
                use_clamp: this.parameters.settings.useClamp,
            },
            resources: {},
        }

        return {value: [mathNode, "value"]}
    }
}
